import axios from 'axios';
import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';

import { appInsights } from '../auth/appInsight';

const useFetch = (url, { method = 'GET', body = null, skip = false, refreshKey = 0 } = {}) => {
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(false);
  const [errorDescription, setErrorDescription] = useState('');
  const [data, setData] = useState([]);
  const activeRequestIdRef = useRef(0);
  const {
    user: { accessToken }
  } = useSelector((state) => state.user);

  const fetchData = async (signal, overrideBody) => {
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;

    setLoading(true);
    setError(false);
    setErrorDescription('');

    const requestBody = overrideBody !== undefined ? overrideBody : body;
    const isPost = method.toUpperCase() === 'POST';

    try {
      const config = { headers: { Authorization: 'Bearer ' + accessToken }, signal };
      const response = isPost ? await axios.post(url, requestBody, config) : await axios.get(url, config);

      if (activeRequestIdRef.current !== requestId || signal?.aborted) return;
      setData(response?.data);
    } catch (err) {
      const isCancelled = err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
      if (activeRequestIdRef.current !== requestId || signal?.aborted || isCancelled) return;

      appInsights.trackException({ error: new Error('url' + url + err) });
      setError(true);
      setErrorDescription(err);
    } finally {
      if (activeRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (skip || !url) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => {
      controller.abort();
    };
  }, [url, skip, refreshKey]);

  try {
    return { loading, error, setError, data, fetchData, errorDescription };
  } catch (error) {
    appInsights.trackException({ error: new Error('fetch url' + url + error) });
  }
};

export default useFetch;
